import type { MetricsClient } from '@strata-monorepo/shared-types'
import { toSnakeCase } from '@strata-monorepo/utils/string'

/**
 * Configuration options for the retry utility
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number
  /** Base delay in milliseconds for exponential backoff (default: 500) */
  baseDelay?: number
  /** Whether to log retry attempts to console (default: true) */
  logAttempts?: boolean
  /** Custom function to determine if an error should trigger a retry */
  shouldRetry?: (error: unknown) => boolean
}

export interface instrumentationOptions {
  requestName: string
  metricsClient?: MetricsClient
}
/**
 * Result of a retry operation containing the result and retry count
 */
export interface RetryResult<TResult> {
  /** The result of the successful operation */
  result: TResult
  /** Number of retries that were needed (0 means no retries needed) */
  retryCount: number
}

/**
 * Result of a retry operation containing the result and retry count
 */
export interface RetryResult<TResult> {
  /** The result of the successful operation */
  result: TResult
  /** Number of retries that were needed (0 means no retries needed) */
  retryCount: number
}

/**
 * Executes an async function with retry logic and exponential backoff
 *
 * @template TResult The return type of the async function
 * @param fn The async function to execute with retries
 * @param options Configuration options for retry behavior
 * @returns Promise that resolves to the retry result containing the function result and retry count
 *
 * @example
 * ```typescript
 * const { result, retryCount } = await withRetry(
 *   () => apolloClient.query({ query: MY_QUERY }),
 *   { maxRetries: 3, baseDelay: 500 }
 * );
 * ```
 */
export async function withRetry<TResult>(
  fn: () => Promise<TResult>,
  options: RetryOptions = {},
  instrumentationOptions?: instrumentationOptions,
): Promise<RetryResult<TResult>> {
  const {
    maxRetries = 3,
    baseDelay = 500,
    logAttempts = false,
    shouldRetry = (): boolean => true,
  } = options

  let retryCount = 0
  let lastError: unknown = null

  const { requestName, metricsClient } = instrumentationOptions || {}
  const requestKey = toSnakeCase(requestName || '')

  while (retryCount < maxRetries) {
    const startTime = Date.now()

    try {
      metricsClient?.increment('strata.request_attempt', 1, {
        request: requestKey,
        attempt: String(retryCount + 1),
      })

      const result = await fn()
      const endTime = Date.now()
      metricsClient?.increment('strata.request_succeeded', 1, {
        request: requestKey,
        duration: String(Math.round(endTime - startTime)),
        attempt: String(retryCount + 1),
      })

      return { result, retryCount }
    } catch (error) {
      const endTime = Date.now()
      metricsClient?.increment('strata.request_failed', 1, {
        request: requestKey,
        reason: 'unknown',
        duration: String(Math.round(endTime - startTime)),
        attempt: String(retryCount + 1),
      })

      lastError = error
      retryCount++

      // Check if we should retry this error
      if (!shouldRetry(error)) {
        throw error
      }

      // Log retry attempts if enabled
      if (logAttempts) {
        console.warn(
          `Retry attempt ${retryCount}/${maxRetries} failed:`,
          error instanceof Error ? error.name : 'Unknown error',
        )
      }

      // If we haven't reached max retries, wait before trying again
      // Exponential backoff: baseDelay, baseDelay*2, baseDelay*4, etc.
      if (retryCount < maxRetries) {
        const delay = baseDelay * Math.pow(2, retryCount - 1)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  // If all retries failed, throw the last error
  if (lastError) {
    throw lastError
  }

  // This should never happen, but TypeScript needs it
  throw new Error('Retry failed without capturing an error')
}
